import type { ZohoDealFieldConflict, ZohoFdDealCommercialContext } from '@pazportal/types';

type FieldTripRow = {
  scope: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  lead_technician: { full_name?: string } | null;
};

/**
 * §C.7 — Resolve the Deal row that maps 1:1 to this field_trip before comparing.
 * When multi-trip deployments link parent/child via Related_Trip_Deal, only the
 * Deal with FD_Supabase_Project_ID = trip.id is compared (never parent/sibling rows).
 */
export function resolveDealForTripComparison(
  zoho: ZohoFdDealCommercialContext,
  supabaseTripId: string,
): ZohoFdDealCommercialContext {
  const linked = zoho.relatedTrips.find((r) => r.supabaseTripId === supabaseTripId);
  if (linked && linked.dealId !== zoho.dealId) {
    return {
      ...zoho,
      dealId: linked.dealId,
      dealName: linked.dealName,
      portalStatus: linked.portalStatus,
      salesStage: linked.salesStage,
      tripStartDate: linked.tripStartDate,
      tripEndDate: linked.tripEndDate,
    };
  }
  return zoho;
}

/**
 * PazPortal (Supabase) is master for field_trips. Zoho FD Deal context is
 * reflective only — disagreements are flagged stale/out-of-band (§E).
 */
export function detectZohoFieldTripConflicts(
  trip: FieldTripRow,
  zoho: ZohoFdDealCommercialContext,
  supabaseTripId: string,
): ZohoDealFieldConflict[] {
  const deal = resolveDealForTripComparison(zoho, supabaseTripId);
  const conflicts: ZohoDealFieldConflict[] = [];

  const leadName = trip.lead_technician?.full_name?.trim() ?? null;
  const crewNames = deal.engineersAssigned.map((e) => e.name.trim().toLowerCase());
  if (leadName && crewNames.length > 0 && !crewNames.includes(leadName.toLowerCase())) {
    conflicts.push({
      field: 'lead',
      supabaseValue: leadName,
      zohoValue: deal.engineersAssigned.map((e) => e.name).join(', '),
    });
  }

  if (trip.start_date && deal.tripStartDate && trip.start_date !== deal.tripStartDate) {
    conflicts.push({
      field: 'start_date',
      supabaseValue: trip.start_date,
      zohoValue: deal.tripStartDate,
    });
  }

  if (trip.end_date && deal.tripEndDate && trip.end_date !== deal.tripEndDate) {
    conflicts.push({
      field: 'end_date',
      supabaseValue: trip.end_date,
      zohoValue: deal.tripEndDate,
    });
  }

  if (trip.status && deal.portalStatus && trip.status !== deal.portalStatus) {
    conflicts.push({
      field: 'portal_status',
      supabaseValue: trip.status,
      zohoValue: deal.portalStatus,
    });
  }

  return conflicts;
}
